import * as React from 'react'
import { MainMenu } from './MainMenu'
import type { MenuData } from './MainMenu'
import { Icon } from '/react4xp/components/primitives/Icon'

export interface HeaderProps {
  logoUrl: string
  siteName?: string
  menu?: MenuData
  heroOverlay?: boolean
}

export const Header: React.FC<HeaderProps> = ({
  logoUrl,
  siteName = 'Fagskolen Diakonova',
  menu,
  heroOverlay = false,
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)
  const [atTop, setAtTop] = React.useState(true)
  const [isSearchOpen, setIsSearchOpen] = React.useState(false)
  const menuButtonRef = React.useRef<HTMLButtonElement>(null)
  const searchButtonRef = React.useRef<HTMLButtonElement>(null)
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  const handleOpenMenu = () => setIsMenuOpen(true)

  const handleCloseMenu = () => {
    setIsMenuOpen(false)
    menuButtonRef.current?.focus()
  }

  const handleOpenSearch = () => setIsSearchOpen(true)

  const handleCloseSearch = () => {
    setIsSearchOpen(false)
    searchButtonRef.current?.focus()
  }

  const handleSokClick = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      handleOpenMenu()
    } else {
      handleOpenSearch()
    }
  }

  React.useEffect(() => {
    if (!heroOverlay) return
    const onScroll = () => setAtTop(window.scrollY < 10)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [heroOverlay])

  React.useEffect(() => {
    if (!isSearchOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCloseSearch()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isSearchOpen])

  React.useEffect(() => {
    if (isSearchOpen) searchInputRef.current?.focus()
  }, [isSearchOpen])

  const showShadow = !(heroOverlay && atTop)

  return (
    <header
      className={
        heroOverlay ? 'absolute top-0 left-0 right-0 z-40' : 'md:mb-14'
      }
    >
      {/* Logo — normal flow on standard pages, absolute overlay on hero pages */}
      <div className="mx-auto max-w-container px-5 md:px-16 py-4 md:py-6">
        <a href="/" aria-label={`${siteName} — til forsiden`}>
          <img src={logoUrl} alt="" className="h-12 md:h-18 w-auto" />
        </a>
      </div>

      {/* Floating nav widget — fixed to viewport, aligned to container */}
      <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
        <div className="mx-auto max-w-container px-5 md:px-16 py-4 md:py-6 flex justify-end">
          <div
            className={`pointer-events-auto flex h-13 items-center rounded-full bg-white px-1.5 ${showShadow ? 'shadow-nav' : ''}`}
            style={{ transition: 'box-shadow 150ms' }}
          >
            {/* Søk pill — collapses when search opens */}
            <button
              ref={searchButtonRef}
              type="button"
              onClick={handleSokClick}
              aria-label="Søk"
              aria-expanded={isSearchOpen}
              aria-controls="header-search"
              inert={isSearchOpen}
              style={{
                transition:
                  'max-width 180ms cubic-bezier(0.33, 1, 0.68, 1), opacity 140ms cubic-bezier(0.33, 1, 0.68, 1), margin-right 180ms cubic-bezier(0.33, 1, 0.68, 1), padding-left 180ms cubic-bezier(0.33, 1, 0.68, 1), padding-right 180ms cubic-bezier(0.33, 1, 0.68, 1)',
              }}
              className={`flex h-10 min-w-0 cursor-pointer items-center gap-3 overflow-hidden whitespace-nowrap rounded-full bg-blue-300 text-xs uppercase tracking-wider text-black focus-visible:outline-1 focus-visible:outline-slate-300 ${
                isSearchOpen
                  ? 'max-w-0 opacity-0 mr-0 pl-0 pr-0 md:pl-0 md:pr-0 pointer-events-none'
                  : 'max-w-52 opacity-100 mr-1 px-5 md:pl-8 md:pr-5'
              }`}
            >
              <span className="hidden md:inline">Søk</span>
              <Icon name="search" />
            </button>

            {/* Search form — expands when search opens */}
            <form
              id="header-search"
              action={menu?.searchPageUrl || '#'}
              method="get"
              inert={!isSearchOpen}
              style={{
                // Match the open-menu panel width (w-3/4 max-w-3xl inside max-w-container px-16)
                // minus the Meny button + FAB inner padding so the expanded FAB has the same
                // right→left extent as the menu.
                width: isSearchOpen
                  ? 'min(calc(75vw - 13rem), 40.5rem)'
                  : '0px',
                transition:
                  'width 180ms cubic-bezier(0.33, 1, 0.68, 1), opacity 140ms cubic-bezier(0.33, 1, 0.68, 1), margin-right 180ms cubic-bezier(0.33, 1, 0.68, 1), padding-left 180ms cubic-bezier(0.33, 1, 0.68, 1), padding-right 180ms cubic-bezier(0.33, 1, 0.68, 1)',
              }}
              className={`flex h-10 min-w-0 items-center gap-1 overflow-hidden rounded-full bg-slate-100 has-[input:focus-visible]:outline-1 has-[input:focus-visible]:outline-slate-300 ${
                isSearchOpen
                  ? 'opacity-100 mr-1 pl-2 pr-2'
                  : 'opacity-0 mr-0 pl-0 pr-0 pointer-events-none'
              }`}
            >
              <button
                type="submit"
                aria-label="Søk"
                className="flex shrink-0 items-center rounded-full text-slate-700 hover:text-black focus-visible:outline-1 focus-visible:outline-slate-300"
              >
                <Icon name="search" />
              </button>
              <input
                ref={searchInputRef}
                type="search"
                name="q"
                placeholder="Søk"
                aria-label="Søk på nettstedet"
                className="min-w-0 flex-1 bg-transparent text-sm text-black placeholder:text-slate-700 focus:outline-none md:text-base"
              />
              <button
                type="button"
                onClick={handleCloseSearch}
                aria-label="Lukk søk"
                className="flex shrink-0 cursor-pointer items-center rounded-full text-slate-700 hover:text-black focus-visible:outline-1 focus-visible:outline-slate-300"
              >
                <Icon name="close" />
              </button>
            </form>

            <button
              ref={menuButtonRef}
              type="button"
              aria-label={isMenuOpen ? 'Lukk meny' : 'Åpne meny'}
              aria-expanded={isMenuOpen}
              onClick={isMenuOpen ? handleCloseMenu : handleOpenMenu}
              className="flex h-10 cursor-pointer items-center gap-4 px-1.5 md:px-3 text-xs uppercase tracking-wider text-black focus-visible:outline-1 focus-visible:outline-slate-300"
            >
              <span className="hidden md:inline">Meny</span>
              <Icon name="dehaze" />
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && menu && <MainMenu menu={menu} onClose={handleCloseMenu} />}
    </header>
  )
}
